/**
 * The grade report, opened over the Assignments page as a side sheet (owner, 2026-10-06: *"open
 * the gra report and a pop up!"*; option A of the mockups, approved).
 *
 * 🔴 IT HAS AN ADDRESS: `?report=<submission id>` on the page's own address. Back closes it, a
 * reload reopens it, and sending the address sends the open report. The standalone page stays
 * at `/submissions/:sid` for Logs, Runs, the Overview and *Open as a page ↗*.
 * 🔴 THE PAGE UNDER IT IS KEPT, NOT RE-READ. The reader's place in the list survives, and so does
 * the Classroom listing an open assignment paid seconds for.
 * 🔴 AN UNSAVED REVIEW IS NEVER THROWN AWAY BY A CLICK. Close, Escape, the scrim and ‹ › ask
 * inside the sheet first — never with `confirm()`, which the console does not use — and Back,
 * which a plain `BrowserRouter` cannot block, is caught here: the address is put back and the
 * same question asked.
 * ⚠️ ‹ › FOLLOW THE LIST ON SCREEN as it stood when the sheet opened. Rows that arrive later join
 * the end and rows that leave stay, so the position never jumps under the reader — a review that
 * moves a grade out of In review does not renumber the rest.
 */
import {
  createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef,
  useState, type KeyboardEvent, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import type { LiveRun, LiveStage, Report } from "../api";
import { clock, Elapsed, since, Spin, useLive } from "../live";
import { routes } from "../routes";
import { DASH } from "../ui";
import { SubmissionReport, type DraftState } from "./Submission";

/** One row a report can be opened from, in the order the list draws it. */
export interface SheetRow { sid: string; student: string; title: string }
interface Listed { rows: SheetRow[]; label: string }

/**
 * The lists on the page, each telling the page what it shows. ⚠️ A REGISTRY, NOT ONE VALUE: a
 * list that unmounts takes its rows with it, and the one most recently drawn is the list a
 * reader is in.
 */
export const ReportLists = createContext<(id: string, list: Listed | null) => void>(() => {});
/** Where a report opens from this page: the page's own address with `report` added. */
export const ReportHref = createContext<(sid: string) => string>((sid) => sid);

/** Tell the page which rows this list shows, in order, so ‹ › can follow it. */
export function useReportList(rows: SheetRow[], label: string): void {
  const set = useContext(ReportLists);
  const id = useId();
  const latest = useRef(rows);
  latest.current = rows;
  const key = `${label}\u0001${rows.map((r) => r.sid).join("\u0000")}`;
  useEffect(() => { set(id, { rows: latest.current, label }); }, [set, id, key, label]);
  useEffect(() => () => set(id, null), [set, id]);
}

/** The registry's state, held by the page that hosts the sheet. */
export function useReportListsHost(): [(id: string, list: Listed | null) => void, Listed] {
  const [lists, setLists] = useState<Map<string, Listed & { at: number }>>(() => new Map());
  const seq = useRef(0);
  const set = useCallback((id: string, list: Listed | null) => {
    setLists((m) => {
      const next = new Map(m);
      if (list) next.set(id, { ...list, at: ++seq.current }); else next.delete(id);
      return next;
    });
  }, []);
  const active = useMemo(() => [...lists.values()].sort((a, b) => b.at - a.at)[0]
    ?? { rows: [], label: "" }, [lists]);
  return [set, active];
}

/** The order ‹ › walk: the list at opening, with later arrivals at the end and nothing dropped. */
export function grow(was: SheetRow[], now: SheetRow[]): SheetRow[] {
  const have = new Set(was.map((r) => r.sid));
  const more = now.filter((r) => !have.has(r.sid));
  return more.length ? [...was, ...more] : was;
}

type Ask = { kind: "close" } | { kind: "step"; sid: string };

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), '
  + 'textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

export function ReportSheet({ courseId, list }: { courseId: string; list: Listed }) {
  const { pathname, search, key: locKey } = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const live = useLive();
  const sid = new URLSearchParams(search).get("report");

  const [shown, setShown] = useState<string | null>(sid);
  const [draft, setDraft] = useState<DraftState>({ editing: false, dirty: false, decided: 0, of: 0 });
  // 🔴 A DISCARD CLEARS THE DRAFT'S STATE AND ITS REF TOGETHER (`forget`). The first version
  // cleared the ref alone and copied the state into it on every render, so the render between a
  // Discard and the navigation it started put the old draft back — and the sheet took its own
  // close for Back and reopened itself (found by its test).
  const dirty = useRef(false);
  const onDraft = useCallback((d: DraftState) => { dirty.current = d.dirty; setDraft(d); }, []);
  const forget = useCallback(() => {
    dirty.current = false;
    setDraft((d) => ({ ...d, dirty: false }));
  }, []);
  const [ask, setAsk] = useState<Ask | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [version, setVersion] = useState(0);
  const [landed, setLanded] = useState<{ points: number | null; max: number | null } | null>(null);
  const [ended, setEnded] = useState<string>("");
  const [copied, setCopied] = useState(false);

  // ── the address ─────────────────────────────────────────────────────────────────────────
  const hrefWith = useCallback((s: string | null) => {
    const q = new URLSearchParams(search);
    if (s) q.set("report", s); else q.delete("report");
    const qs = q.toString();
    return `${pathname}${qs ? `?${qs}` : ""}`;
  }, [pathname, search]);
  // Whether the sheet was opened by a link on this very page, and is still that entry: then
  // closing is Back, and the history holds no second copy of the list.
  const prev = useRef<{ pathname: string; report: string | null } | null>(null);
  const pushedKey = useRef<string | null>(null);
  const lastOpen = useRef<string>("");
  useEffect(() => {
    const was = prev.current;
    if (sid && !was?.report && navType === "PUSH" && was?.pathname === pathname) {
      pushedKey.current = locKey;
    } else if (!sid) {
      pushedKey.current = null;
    }
    prev.current = { pathname, report: sid };
    if (sid) lastOpen.current = `${pathname}${search}`;
  }, [sid, pathname, search, locKey, navType]);

  useEffect(() => {
    if (sid) { setShown(sid); return; }
    // 🔴 BACK TOOK THE ADDRESS AWAY WITH A REVIEW HALF-WRITTEN: put it back and ask.
    if (dirty.current && lastOpen.current) {
      navigate(lastOpen.current);
      setAsk({ kind: "close" });
      return;
    }
    setShown(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  // ── the order ‹ › walk ──────────────────────────────────────────────────────────────────
  const [order, setOrder] = useState<Listed | null>(null);
  useEffect(() => {
    if (!shown) { setOrder(null); return; }
    setOrder((o) => (o === null || o.rows.length === 0
      ? { rows: list.rows, label: list.label }
      : { rows: grow(o.rows, list.rows), label: o.label }));
  }, [shown, list]);
  const rows = order?.rows ?? [];
  const at = shown ? rows.findIndex((r) => r.sid === shown) : -1;
  const here = at >= 0 ? rows[at]! : null;
  const before = at > 0 ? rows[at - 1]! : null;
  const after = at >= 0 && at < rows.length - 1 ? rows[at + 1]! : null;

  // ── acts, each through the guard ────────────────────────────────────────────────────────
  const close = useCallback(() => {
    forget();
    setAsk(null);
    if (pushedKey.current && pushedKey.current === locKey) navigate(-1);
    else navigate(hrefWith(null), { replace: true });
  }, [forget, hrefWith, locKey, navigate]);
  const step = useCallback((to: string) => {
    forget();
    setAsk(null);
    navigate(hrefWith(to), { replace: true });
  }, [forget, hrefWith, navigate]);
  // A replace makes a new entry key; it is still the entry the list's link pushed.
  useEffect(() => {
    if (pushedKey.current && navType === "REPLACE" && sid) pushedKey.current = locKey;
  }, [locKey, navType, sid]);
  const askClose = () => (dirty.current ? setAsk({ kind: "close" }) : close());
  const askStep = (to: string) => (dirty.current ? setAsk({ kind: "step", sid: to }) : step(to));

  // ── a new run over this submission ──────────────────────────────────────────────────────
  const run = shown ? live.feed?.runs.find((r) => r.submission_id === shown) : undefined;
  const seenEvent = useRef(0);
  useEffect(() => {
    setLanded(null); setEnded(""); setReport(null); setCopied(false);
    dirty.current = false;
    setDraft({ editing: false, dirty: false, decided: 0, of: 0 });
    seenEvent.current = live.events.length ? live.events[live.events.length - 1]!.n : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);
  useEffect(() => {
    if (!shown) return;
    const mine = live.events.filter((e) => e.n > seenEvent.current
                                           && e.item.submission_id === shown);
    if (live.events.length) seenEvent.current = live.events[live.events.length - 1]!.n;
    const last = mine[mine.length - 1];
    if (!last) return;
    if (last.item.outcome === "recorded") {
      // 🔴 WITH NOTHING DRAFTED THE NEW GRADE SIMPLY SHOWS. With a draft, the draft belongs to the
      // grade it was written against — the server refuses to save it against the new one — so
      // the banner offers the new grade and the reader decides when.
      if (dirty.current) setLanded({ points: last.item.points, max: last.item.max_points });
      else setVersion((v) => v + 1);
    } else {
      setEnded(last.item.detail || last.item.outcome);
    }
  }, [live.events, shown]);

  // ── focus, the page under it, and the keys ──────────────────────────────────────────────
  const host = useMemo(() => {
    const el = document.createElement("div");
    el.className = "sheet-root";
    return el;
  }, []);
  const heading = useRef<HTMLHeadingElement>(null);
  const opener = useRef<Element | null>(null);
  const open = shown !== null;
  useLayoutEffect(() => {
    if (!open) return;
    opener.current = document.activeElement;
    document.body.appendChild(host);
    // 🔴 THE PAGE UNDER IT IS `inert`: no click, no Tab and no screen reader reaches it. Only what
    // this set, so an element already inert for its own reason stays that way.
    const made: Element[] = [];
    for (const el of [...document.body.children]) {
      if (el !== host && !el.hasAttribute("inert")) { el.setAttribute("inert", ""); made.push(el); }
    }
    const html = document.documentElement;
    const was = html.style.overflow;
    html.style.overflow = "hidden";
    heading.current?.focus();
    return () => {
      for (const el of made) el.removeAttribute("inert");
      html.style.overflow = was;
      host.remove();
    };
  }, [open, host]);
  // Focus goes back to the row the reader is on now — the last report shown, after ‹ › — or to
  // whatever opened the sheet.
  const lastShown = useRef<string | null>(null);
  useEffect(() => { if (shown) lastShown.current = shown; }, [shown]);
  useEffect(() => {
    if (open) return;
    const s = lastShown.current;
    if (!s) return;
    const row = document.querySelector<HTMLElement>(
      `a[href*="report=${encodeURIComponent(s)}"]`);
    const back = row ?? (opener.current instanceof HTMLElement ? opener.current : null);
    back?.focus();
  }, [open]);

  if (!shown) return null;

  const name = here?.student || report?.student || "Unnamed student";
  const title = here?.title || report?.assignment_title || "";
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Escape" && !e.defaultPrevented) {
      e.preventDefault();
      if (ask) setAsk(null); else askClose();
    }
  };
  const wrap = (to: "first" | "last") => {
    const box = host.querySelector(".sheet");
    if (!box) return;
    const all = [...box.querySelectorAll<HTMLElement>(FOCUSABLE)]
      .filter((el) => !el.classList.contains("sheet-edge"));
    const seen = all.filter((el) => el.getClientRects().length > 0);
    const pick = seen.length ? seen : all;
    (to === "first" ? pick[0] : pick[pick.length - 1])?.focus();
  };

  return createPortal(
    <>
      <div className="sheet-scrim" onClick={askClose} aria-hidden="true" />
      <section className="sheet" role="dialog" aria-modal="true" aria-labelledby="sheet-h"
               onKeyDown={onKey}>
        {/* A sheet with one way out is a trap, and Tab falling off its end is a way out. */}
        <span className="sheet-edge" tabIndex={0} onFocus={() => wrap("last")} />
        <header className="sheet-head">
          <div className="sheet-nav">
            <button type="button" className="btn sm icon" disabled={!before}
                    aria-label={`Previous one ${order?.label ?? ""}`.trim()}
                    onClick={() => before && askStep(before.sid)}>‹</button>
            {at >= 0 && (
              <span className="sheet-pos">{at + 1} of {rows.length}
                {order?.label ? <> {order.label}</> : null}</span>)}
            <button type="button" className="btn sm icon" disabled={!after}
                    aria-label={`Next one ${order?.label ?? ""}`.trim()}
                    onClick={() => after && askStep(after.sid)}>›</button>
          </div>
          <div className="sheet-title">
            <span className="eyebrow">Grade report</span>
            <h2 id="sheet-h" ref={heading} tabIndex={-1}>
              {name}{title && <span className="sheet-of">{" — "}{title}</span>}</h2>
          </div>
          <div className="sheet-acts">
            {draft.dirty && <span className="badge warn">unsaved review</span>}
            <a className="btn sm ghost" href={routes.submission(courseId, shown)} target="_blank"
               rel="noreferrer">Open as a page ↗</a>
            <button type="button" className="btn sm ghost"
                    onClick={() => {
                      // ⚠️ NO CLIPBOARD OUTSIDE A SECURE CONTEXT: `?.` alone would call `.then` on
                      // nothing and throw.
                      (navigator.clipboard?.writeText(`${window.location.origin}${hrefWith(shown)}`)
                        ?? Promise.reject(new Error("no clipboard")))
                        .then(() => setCopied(true)).catch(() => setCopied(false));
                    }}>{copied ? "Copied" : "Copy link"}</button>
          </div>
          <button type="button" className="btn sm icon sheet-x" aria-label="Close the report"
                  onClick={askClose}>×</button>
        </header>

        {ask && (
          <div className="alert warn sheet-ask" role="alertdialog" aria-labelledby="sheet-ask-t">
            <span className="ico">⚠️</span>
            <div className="body">
              <div className="t" id="sheet-ask-t">Discard your review of {name}?</div>
              <div className="m">Nothing of it has been saved.</div>
            </div>
            <div className="sheet-ask-acts">
              <button type="button" className="btn sm" autoFocus onClick={() => setAsk(null)}>
                Keep editing</button>
              <button type="button" className="btn sm danger"
                      onClick={() => (ask.kind === "close" ? close() : step(ask.sid))}>
                Discard</button>
            </div>
          </div>)}

        <Banner run={run} landed={landed} ended={ended} drafting={draft.dirty}
                onShow={() => { forget(); setLanded(null); setVersion((v) => v + 1); }} />

        <div className="sheet-scroll">
          <SubmissionReport key={`${shown}|${version}`} courseId={courseId} submissionId={shown}
                            variant="sheet" onDraft={onDraft} onReport={setReport} />
        </div>

        {/* On a phone the verdict panel comes after every criterion, so the dock says where it
            is and how far the review has got. */}
        {draft.editing && (
          <div className="sheet-dock">
            <span className="sheet-dock-t"><b>Your verdict</b>{draft.decided} of {draft.of} decided
              · after the criteria</span>
            <button type="button" className="btn sm"
                    onClick={() => host.querySelector('[aria-label="This grade"]')
                      ?.scrollIntoView?.({ block: "start" })}>Go to verdict ↓</button>
          </div>)}
        <span className="sheet-edge" tabIndex={0} onFocus={() => wrap("first")} />
      </section>
    </>,
    host);
}

/** The banner's sentence for each stage of a new run — written out, because the row's words
 *  ("Waiting for a grader") are labels and do not make a sentence about "this submission". */
const RUN_SAYS: Record<Exclude<LiveStage, "held">, string> = {
  starting: "A new run is starting for this submission",
  waiting: "A new run is waiting for a grader",
  grading: "A new run is grading this submission",
  checking: "A new run's citations are being checked",
};

/** What a new run over this submission is doing, or did. */
function Banner({ run, landed, ended, drafting, onShow }: {
  run: LiveRun | undefined;
  landed: { points: number | null; max: number | null } | null;
  ended: string;
  drafting: boolean;
  onShow: () => void;
}): ReactNode {
  if (landed) {
    return (
      <div className="alert info sheet-banner" role="status">
        <span className="ico">✓</span>
        <div className="body">
          <div className="t">A new grade landed · {landed.points ?? DASH}/{landed.max ?? DASH}</div>
          <div className="m">Your review was written against the grade below, and the server will
            not save it against the new one.</div>
        </div>
        <button type="button" className="btn sm" onClick={onShow}>Show it</button>
      </div>);
  }
  if (run) {
    return (
      <div className="alert info sheet-banner" role="status">
        <span className="ico"><Spin dim={run.stage === "starting" || run.stage === "waiting"} /></span>
        <div className="body">
          <div className="t">{run.stage === "held"
            ? <>A new run is being checked again{run.retry_finalise_at
                ? <> at {clock(run.retry_finalise_at)}</> : null}.</>
            : <>{RUN_SAYS[run.stage]}
                {run.stage !== "starting" && <>{" · "}<Elapsed since={since(run)} /></>}.</>}</div>
          <div className="m">{drafting
            ? "A review you save before it lands applies to this grade only; the new one will need its own."
            : "When it lands, this report shows the new grade."}</div>
        </div>
      </div>);
  }
  if (ended) {
    return (
      <div className="alert sheet-banner" role="status">
        <span className="ico">⚠️</span>
        <div className="body">
          <div className="t">The new run ended without a grade, so this one stands.</div>
          <div className="m">{ended}</div>
        </div>
      </div>);
  }
  return null;
}
